/**
 * ETF screener types. Data comes from the internal gateway's
 * /internal/etf/screener through our own proxy (/data/etf-screener).
 * Field semantics: docs in the ETF integration handover, Part A §4.1 / §12.
 */

/** Akinda fund screen verdict (AAOIFI-derived), stored in `status`. */
export type EtfFundStatus = "HALAL" | "NOT HALAL" | "DOUBTFUL" | "UNQUALIFIED";

export const ETF_METHODOLOGIES = ["aaoifi", "djim", "ftse", "msci", "sp"] as const;
export type EtfMethodology = (typeof ETF_METHODOLOGIES)[number];

/** The only sort keys the backend accepts; anything else is a 400. */
export const ETF_SORT_KEYS = [
  "ticker",
  "fund_name",
  "aum",
  "expense_ratio",
  "coverage_weight_perc",
  "non_compliant_weight_perc",
  "holdings_total",
  "last_updated",
] as const;
export type EtfSortKey = (typeof ETF_SORT_KEYS)[number];
export type EtfSortOrder = "asc" | "desc";

/**
 * One fund row, built by walking a declared key list (see normalize), so a
 * column the pipeline adds upstream cannot reach the page unreviewed.
 */
export interface EtfRow {
  ticker: string;
  fundName: string | null;
  issuer: string | null;
  /** Domicile, NOT where it trades. GB never occurs. */
  listingCountry: string | null;
  /** Where it trades, from the ticker suffix: US, GB, CA, or null. */
  venue: "US" | "GB" | "CA" | null;
  reportingCurrency: string | null;
  /** Already a percent: 0.45 means 0.45%. */
  expenseRatio: number | null;
  /** In reportingCurrency; GBp rows are pence. */
  aum: number | null;
  status: string | null;
  statusDjim: string | null;
  statusFtse: string | null;
  statusMsci: string | null;
  statusSp: string | null;
  compliantWeightPerc: number | null;
  nonCompliantWeightPerc: number | null;
  doubtfulWeightPerc: number | null;
  unscreenedWeightPerc: number | null;
  coverageWeightPerc: number | null;
  holdingsTotal: number | null;
  holdingsScreened: number | null;
  /** Naive date (YYYY-MM-DD), the provider's basket date. */
  holdingsAsOf: string | null;
  /** Naive date-time, no zone. */
  lastUpdated: string | null;
}

export interface EtfScreenerResponse {
  data: EtfRow[];
  total: number;
  limit: number;
  offset: number;
}

/** Query parameters our proxy accepts (page/limit, not offset). */
export interface EtfScreenerQuery {
  page: number;
  limit: number;
  status?: string;
  methodology?: string;
  venue?: string;
  domicile?: string;
  min_coverage?: string;
  max_expense_ratio?: string;
  min_aum?: string;
  issuer?: string;
  q?: string;
  sort?: EtfSortKey;
  order?: EtfSortOrder;
}
